import React, { useState, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { GlassValueDisplay } from '../../components/common/GlassValueDisplay';
import { ExpandableText } from '../../components/common/ExpandableText';
import { InlineDateEditor } from '../../components/production-line/InlineDateEditor';
import { RackCell } from '../../components/common/RackCell';
import { TemporaryRackCell } from '../../components/common/TemporaryRackCell';
import { OrderImagesCells } from '../../components/common/OrderImagesCells';
import { PAGE_COLORS } from '../../utils/pageColors';
import { formatCurrencyGBP } from '../../utils/formatNumbers';
import { useGetGlassArrivalJobsQuery, useUpdateOrderMutation, useGetGlassValuesDropdownQuery } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { DEFAULT_ORDER_COLUMNS } from '../../utils/orderTableConfig';
import { getVisibleColumns, getNestedValue, isGlassValueArrival, orderHasImagesColumnData } from '../../utils/orderTableHelpers';
import { isOrderGAP } from '../../utils/gapDetection';
import { getGlassArrivalRackPolicy } from '../../utils/rackPolicy';
import type { OrderTableColumn } from '../../types/components';
import { Package, AlertTriangle, Search, Filter, Calendar, Truck, Check } from 'lucide-react';

export const GlassArrivalJobsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0] // Today's date as default
  );
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  
  const [updateOrder] = useUpdateOrderMutation();
  const toast = useToast();

  // Filter parameters (full list; no pagination)
  const filters = useMemo(() => ({
    glass_arrival_date: selectedDate, // Filter by exact glass arrival date
    search: searchTerm || undefined,
    job_type: jobTypeFilter !== 'all' ? jobTypeFilter : undefined,
    ordering: 'glass_arrival_date,-created_at',
  }), [selectedDate, searchTerm, jobTypeFilter]);

  // Fetch Glass Arrival jobs data
  const { data: glassArrivalData, isLoading, isError } = useGetGlassArrivalJobsQuery(filters);

  const jobs = glassArrivalData?.results ?? [];

  // Extract and prepare orders for table display (similar to Production Line approach)
  const orders = useMemo(() => {
    const seenJobIds = new Set<string>();
    
    // Step 1: Sort jobs by potential_delivery_date (earliest first)
    const sortedJobs = [...jobs].sort((a, b) => {
      const dateA = a.potential_delivery_date ? new Date(a.potential_delivery_date).getTime() : Infinity;
      const dateB = b.potential_delivery_date ? new Date(b.potential_delivery_date).getTime() : Infinity;
      return dateA - dateB; // Ascending order (earliest date first)
    });
    
    return sortedJobs.flatMap(job => {
      // Step 2: Sort orders within this job by created_at (oldest first)
      const sortedOrders = [...job.orders].sort((a: any, b: any) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateA - dateB; // Ascending order (oldest first)
      });
      
      return sortedOrders.map((order: any) => {
        const isFirstOrderFromJob = !seenJobIds.has(job.id);
        if (isFirstOrderFromJob) {
          seenJobIds.add(job.id);
        }
        
        return {
          ...order,
          // Add job context for display
          job_id: job.id,
          customer_name: job.customer_name,
          website_job_number: job.website_job_number,
          delivery_postcode: job.delivery_postcode,
          glass_arrival_date: job.glass_arrival_date,
          manufacture_date: job.manufacture_date,
          potential_delivery_date: job.potential_delivery_date,
          job_priority: job.priority,
          job_type: job.job_type,
          job_status: job.status,
          job_production_status: job.production_status,
          // Mark first order for job grouping
          is_first_order_from_job: isFirstOrderFromJob,
        };
      });
    });
  }, [jobs]);


  // Checkbox handlers - Following Production Line pattern (persist in order.metadata)
  const handleScratchedChange = async (order: any, checked: boolean) => {
    try {
      const current = order.metadata?.glass_arrival_workflow || {};
      const next = {
        ...current,
        scratched: !!checked,
        ...(checked ? { arrived: false } : {}),
        scratched_at: checked ? new Date().toISOString() : null,
      };
      const updatedMetadata = { ...order.metadata, glass_arrival_workflow: next };
      
      await updateOrder({ 
        id: order.id, 
        data: { metadata: updatedMetadata }
      }).unwrap();
      
      if (checked) {
        toast.success('Scratched', 'Order marked as scratched.');
      } else {
        toast.success('Scratched removed', 'Scratched status removed.');
      }
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    }
  };

  const handleArrivedChange = async (order: any, checked: boolean) => {
    try {
      const current = order.metadata?.glass_arrival_workflow || {};
      const next = {
        ...current,
        arrived: !!checked,
        ...(checked ? { scratched: false } : {}),
        arrived_at: checked ? new Date().toISOString() : null,
      };
      const updatedMetadata = { ...order.metadata, glass_arrival_workflow: next };
      
      await updateOrder({ 
        id: order.id, 
        data: { metadata: updatedMetadata }
      }).unwrap();
      
      if (checked) {
        toast.success('Arrived', 'Order marked as glass arrived.');
      } else {
        toast.success('Arrival removed', 'Arrival status removed.');
      }
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    }
  };

  // Glass Arrival Table Component (only affects this page)
  const GlassArrivalTable: React.FC<{ orders: any[]; loading: boolean }> = ({ orders, loading }) => {
    // Fetch glass values for lookup
    const { data: glassValuesData } = useGetGlassValuesDropdownQuery({});
    
    // Create lookup map from glass values data
    const glassValuesLookup = useMemo(() => {
      if (glassValuesData?.results) {
        const lookup = new Map<string, { is_arrival: boolean; is_cutting: boolean }>();
        glassValuesData.results.forEach((gv) => {
          // Ensure key is a string (glass value ID)
          const key = String(gv.value);
          lookup.set(key, { is_arrival: gv.is_arrival, is_cutting: gv.is_cutting });
        });
        return lookup;
      }
      return undefined;
    }, [glassValuesData]);
    
    // Column order: job refs → size & glass specs & qty → dates → images (workflow cols appended in thead)
    const allColumns: OrderTableColumn[] = useMemo(() => {
      const fromDefault = (key: string): OrderTableColumn => {
        const col = DEFAULT_ORDER_COLUMNS.find((c) => c.key === key);
        if (!col) {
          throw new Error(`Glass Arrival: missing DEFAULT_ORDER_COLUMNS entry for "${key}"`);
        }
        return col;
      };
      return [
        { key: 'website_job_number', label: 'W Order No.', type: 'text', group: 'job_context' },
        { key: 'delivery_postcode', label: 'PostCode', type: 'text', group: 'job_context', width: '100px' },
        fromDefault('product_type'),
        fromDefault('glass_width'),
        fromDefault('glass_height'),
        fromDefault('diameter_mm'),
        fromDefault('metadata.glass_side_one_value_id'),
        fromDefault('metadata.glass_side_two_value_id'),
        fromDefault('metadata.middle_glass_panel_value_id'),
        fromDefault('quantity'),
        { key: 'glass_arrival_date', label: 'Glass Arrival', type: 'date', group: 'dates' },
        { key: 'manufacture_date', label: 'Date of Manufacture', type: 'date', group: 'dates' },
        { key: 'potential_delivery_date', label: 'Potential Delivery', type: 'text', group: 'dates' },
        { key: 'images_available', label: 'Image', type: 'component', group: 'images', width: '80px', condition: { hasDataCheck: orderHasImagesColumnData } },
        { key: 'image_types', label: 'Image Type', type: 'component', group: 'images', width: '200px', condition: { hasDataCheck: orderHasImagesColumnData } },
        { key: 'image_actions', label: 'View', type: 'component', group: 'images', width: '80px', condition: { hasDataCheck: orderHasImagesColumnData } },
      ];
    }, []);

    // Apply auto-hide logic to get visible columns
    const visibleColumns = useMemo(() => {
      return getVisibleColumns(orders, allColumns);
    }, [orders, allColumns]);

    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (!orders || orders.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <div className="text-sm">No orders available</div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="p-2 border text-left font-medium text-gray-700"
                  style={column.width ? { width: column.width } : undefined}
                >
                  {column.label}
                </th>
              ))}
              <th className="p-2 border text-center font-medium text-gray-700" style={{ width: '120px' }}>Scratched</th>
              <th className="p-2 border text-center font-medium text-gray-700" style={{ width: '120px' }}>Arrived</th>
              <th className="p-2 border text-center font-medium text-gray-700" style={{ width: '120px' }}>Temporary Rack</th>
              <th className="p-2 border text-center font-medium text-gray-700" style={{ width: '120px' }}>Rack</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map((order, index) => {
              // Read checkbox state from order.metadata (Production Line pattern)
              const isScratched = order.metadata?.glass_arrival_workflow?.scratched ?? false;
              const isArrived = order.metadata?.glass_arrival_workflow?.arrived ?? false;
              
              // Check if this is the first order from a new job (for visual grouping)
              const previousOrder = index > 0 ? orders[index - 1] : null;
              const isNewJobGroup = !previousOrder || previousOrder.job_id !== order.job_id;
              
              // Dynamic row background color
              // Priority order (highest to lowest):
              // 1. Green (Arrived) - TOP MOST priority
              // 2. Red (Scratched) - Second priority
              // 3. Yellow (GAP) - Third priority
              // 4. White (Default) - No checkboxes
              // Note: Scratched and Arrived are mutually exclusive (one unchecks the other)
              let rowBgClass = '';
              if (isArrived) {
                rowBgClass = 'bg-green-100 hover:bg-green-200';
              } else if (isScratched) {
                rowBgClass = 'bg-red-100 hover:bg-red-200';
              } else if (isOrderGAP(order, glassValuesLookup)) {
                rowBgClass = 'bg-yellow-100';
              }
              
              // Add visual job separator for new jobs
              if (isNewJobGroup && index > 0) {
                rowBgClass += ' border-t-2 border-blue-200';
              }

              // Track if image columns have been rendered (to avoid rendering them multiple times)
              let imageColumnsRendered = false;

              return (
                <tr key={order.id} className={rowBgClass}>
                  {visibleColumns.map((column) => {
                    const value = getNestedValue(order, column.key);
                    
                    // Handle image columns specially - render all three at once
                    const imageColumns = ['images_available', 'image_types', 'image_actions'];
                    if (imageColumns.includes(column.key)) {
                      if (!imageColumnsRendered) {
                        imageColumnsRendered = true;
                        return (
                          <OrderImagesCells
                            key={`images-${order.id}`}
                            orderId={String(order.id ?? '')}
                            order={order}
                          />
                        );
                      } else {
                        // Skip subsequent image columns as they're rendered by OrderImagesCells
                        return null;
                      }
                    }
                    
                    // Special handling for content inside <td>
                    if (
                      (column.key.includes('glass_side') && column.key.includes('value_id')) ||
                      column.key === 'metadata.middle_glass_panel_value_id'
                    ) {
                      // Glass value columns - show actual glass type names; fallback to woo_glass_raw
                      const wooGlass = (order as any).metadata?.woo_glass_raw;
                      const rawFallback =
                        column.key.includes('one') ? wooGlass?.side_one :
                        column.key.includes('two') ? wooGlass?.side_two :
                        column.key.includes('three') || column.key.includes('middle') ? wooGlass?.side_three :
                        undefined;
                      const wooPatterned = (order as any).metadata?.woo_patterned_glass;
                      const patternId = (k: string) => {
                        const v = (order as any).metadata?.[k];
                        return typeof v === 'string' && v ? v : undefined;
                      };
                      let patternedOptionId: string | undefined;
                      let patternedRawFallback: string | undefined;
                      if (column.key.includes('one')) {
                        patternedOptionId = patternId('patterned_gs_one_option_id');
                        patternedRawFallback = wooPatterned?.one;
                      } else if (column.key.includes('two')) {
                        patternedOptionId = patternId('patterned_gs_two_option_id');
                        patternedRawFallback = wooPatterned?.two;
                      } else if (column.key.includes('three') || column.key.includes('middle')) {
                        patternedOptionId = patternId('patterned_gs_three_option_id');
                        patternedRawFallback = wooPatterned?.three;
                      }
                      const isArrival = isGlassValueArrival(order, column.key, value, glassValuesLookup);
                      const cellBgClass = isArrival ? 'bg-blue-100' : '';
                      
                      return (
                        <td key={column.key} className={`p-2 border ${cellBgClass}`}>
                          <GlassValueDisplay
                            glassValueId={value}
                            rawFallback={rawFallback}
                            patternedOptionId={patternedOptionId}
                            patternedRawFallback={patternedRawFallback}
                          />
                        </td>
                      );
                    }
                    
                    if (['glass_arrival_date', 'manufacture_date', 'potential_delivery_date'].includes(column.key)) {
                      // Date columns - inline editable (only on first order from job)
                      const isReadOnly = !(order as any).is_first_order_from_job;
                      
                      return (
                        <td key={column.key} className="p-2 border">
                          <InlineDateEditor
                            jobId={order.job_id}
                            field={column.key as keyof import('../../utils/types').JobDatesData}
                            value={value}
                            dates={{
                              glass_arrival_date: (order as any).glass_arrival_date,
                              manufacture_date: (order as any).manufacture_date,
                              potential_delivery_date: (order as any).potential_delivery_date
                            }}
                            jobStatus={(order as any).job_status}
                            productionStatus={(order as any).job_production_status}
                            isReadOnly={isReadOnly}
                          />
                        </td>
                      );
                    }
                    
                    // Standard column formatting
                    let displayValue: React.ReactNode = value;
                    
                    // Use custom render function if provided
                    if (column.render) {
                      displayValue = column.render(value, order);
                    } else if (
                      column.type === 'text' &&
                      column.textDisplay?.expandable
                    ) {
                      displayValue = (
                        <ExpandableText
                          text={value != null ? String(value) : ''}
                          collapsedLines={column.textDisplay.collapsedLines ?? 3}
                          buttonClassName="text-blue-600 hover:text-blue-800 text-xs font-medium mt-0.5"
                        />
                      );
                    } else if (column.key === 'potential_delivery_date' && value) {
                      displayValue = new Date(value).toLocaleString();
                    } else if (column.type === 'date' && value) {
                      displayValue = new Date(value).toLocaleDateString();
                    } else if (column.type === 'currency' && value) {
                      displayValue = formatCurrencyGBP(parseFloat(value));
                    } else if (column.type === 'boolean') {
                      displayValue = value ? 'Yes' : 'No';
                    } else if (!value && value !== 0) {
                      displayValue = '-';
                    }

                    return (
                      <td
                        key={column.key}
                        className={[
                          'p-2 border',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right'
                        ].filter(Boolean).join(' ')}
                      >
                        {displayValue}
                      </td>
                    );
                  })}
                  
                  {/* Scratched Checkbox - Production Line Style */}
                  <td className="p-2 border text-center">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center">
                        <label className="relative cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={isScratched}
                            onChange={(e) => handleScratchedChange(order, e.target.checked)}
                            className="sr-only"
                          />
                          <div 
                            className={`
                              w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                              ${isScratched 
                                ? 'bg-red-500 border-red-500' 
                                : 'border-gray-300 hover:border-red-400 group-hover:bg-red-50'
                              }
                              cursor-pointer
                            `}
                          >
                            {isScratched && (
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            )}
                          </div>
                        </label>
                      </div>
                    </div>
                  </td>
                  
                  {/* Arrived Checkbox - Production Line Style */}
                  <td className="p-2 border text-center">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center">
                        <label className="relative cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={isArrived}
                            onChange={(e) => handleArrivedChange(order, e.target.checked)}
                            className="sr-only"
                          />
                          <div 
                            className={`
                              w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                              ${isArrived 
                                ? 'bg-green-500 border-green-500' 
                                : 'border-gray-300 hover:border-green-400 group-hover:bg-green-50'
                              }
                              cursor-pointer
                            `}
                          >
                            {isArrived && (
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            )}
                          </div>
                        </label>
                      </div>
                    </div>
                  </td>
                  
                  {/* Temporary Rack Column - policy-driven */}
                  {(() => {
                    const policy = getGlassArrivalRackPolicy(order, isArrived);
                    return policy.temporaryRack.showEditor ? (
                      <TemporaryRackCell key="temporary_rack" order={order} />
                    ) : (
                      <td className="p-2 border text-center">
                        <div className="flex items-center justify-center">
                          <span className="text-gray-400 text-sm italic">{policy.temporaryRack.placeholder}</span>
                        </div>
                      </td>
                    );
                  })()}
                  
                  {/* Rack Column - policy-driven */}
                  {(() => {
                    const policy = getGlassArrivalRackPolicy(order, isArrived);
                    if (policy.rack.showEditor) {
                      return <RackCell key="rack" order={order} />;
                    }
                    return (
                      <td className="p-2 border text-center">
                        <div className="flex items-center justify-center">
                          <span className="text-gray-400 text-sm italic">{policy.rack.placeholder}</span>
                        </div>
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setJobTypeFilter('all');
  };

  if (isError) {
    return (
      <AppShell>
        <PageLayout
          title="Glass Arrival Jobs"
          subtitle="Track glass arrivals and jobs"
          bgColor={PAGE_COLORS.production.bgColor}
          textColor={PAGE_COLORS.production.textColor}
          borderColor={PAGE_COLORS.production.borderColor}
          icon={<Truck className="w-6 h-6" />}
        >
          <ContentCard title="Error Loading Glass Arrival Jobs">
            <div className="text-center py-6">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <p className="text-gray-600 mb-4">Unable to load glass arrival jobs data</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageLayout
        title="Glass Arrival Jobs"
        subtitle="Track glass arrivals and jobs"
        bgColor={PAGE_COLORS.production.bgColor}
        textColor={PAGE_COLORS.production.textColor}
        borderColor={PAGE_COLORS.production.borderColor}
        icon={<Truck className="w-6 h-6" />}
      >
        {/* Filters */}
        <div className="mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Search */}
              <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search customer, order reference, postcode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10 w-full"
                  />
                </div>
              </form>

              {/* Filters Label */}
              <div className="hidden sm:flex items-center gap-2 text-gray-700">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Filters:</span>
              </div>

              {/* Date Filter */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="form-input w-auto"
                />
              </div>

              {/* Job Type Filter */}
              <select
                value={jobTypeFilter}
                onChange={(e) => setJobTypeFilter(e.target.value)}
                className="form-select w-auto"
              >
                <option value="all">All Job Types</option>
                <option value="ga">Glass Arrival</option>
                <option value="gc">Glass Cutting</option>
                <option value="gap">Glass Processing</option>
              </select>

              {/* Clear Filters */}
              {(searchTerm || jobTypeFilter !== 'all') && (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <ContentCard
          title="Glass Arrival Jobs & Orders"
          subtitle={`Orders for glass arrivals on ${new Date(selectedDate).toLocaleDateString()}`}
        >
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : orders.length === 0 ? (
            <EmptyState
              icon={<Package className="h-12 w-12 text-gray-400" />}
              title="No glass arrivals found"
              description={`No jobs with glass arrival date of ${new Date(selectedDate).toLocaleDateString()} found.`}
            />
          ) : (
            <>
              <GlassArrivalTable 
                orders={orders}
                loading={isLoading}
              />
            </>
          )}
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};
